import { isEqual } from 'lodash';
import { proxy } from 'valtio';
import { getProjectDownloads as apiGetProjectDownloads } from '~/api/projects2';
import type { TLoadedState } from '~/common/types';
import type { ProjectDownload } from './common';
import type { RecordedSubmission } from './extract/common';

class Store {
  downloads: ProjectDownload[] = [];
  hoveredDownload: string | null = null;
  /**
   * A submission accepted by the API whose rows have not been fetched yet. The extract job creates
   * them out of band, so the history has to keep polling until they land or nothing would show the
   * run the user just started.
   */
  awaitingSubmissionId: string | null = null;
  /**
   * The last submission this session sent, so its cooldown can be enforced. In-memory only — a
   * reload clears it, and the server does not enforce the cooldown yet.
   */
  lastSubmission: RecordedSubmission | null = null;
  /**
   * Sequence number of the most recently started fetch. The panel refreshes from three places at
   * once — mount, submission, and the poll — so responses can land out of order.
   */
  latestFetchId = 0;

  async getProjectDownloads(projectId: string) {
    const fetchId = ++this.latestFetchId;
    const newDownloads = (await apiGetProjectDownloads(projectId)) as TLoadedState & ProjectDownload[];

    // Discards a response a later fetch has already superseded, rows and awaited submission alike.
    if (fetchId !== this.latestFetchId) return;

    // not sure if this even works might might need to remove
    if (!isEqual(this.downloads, newDownloads)) {
      this.downloads = newDownloads;
    }

    if (this.awaitingSubmissionId && newDownloads.some((d) => d.submissionId === this.awaitingSubmissionId)) {
      this.awaitingSubmissionId = null;
    }
  }

  setAwaitingSubmission(submissionId: string | null) {
    this.awaitingSubmissionId = submissionId ?? null;
  }

  recordSubmission(projectId: string) {
    this.lastSubmission = { projectId, at: Date.now() };
  }

  setHoveredDownload(id: string | null) {
    this.hoveredDownload = id;
  }

  /** Clears the history panel's view state. `lastSubmission` outlives it — the cooldown is
   * session-scoped, not panel-scoped. */
  reset() {
    this.downloads = [];
    this.hoveredDownload = null;
    this.awaitingSubmissionId = null;
    // Retires any fetch still in flight, so it can't repopulate the panel after unmount.
    this.latestFetchId++;
  }
}

const downloadStore = proxy(new Store());
export default downloadStore;
